/**
 * Social Account Service
 *
 * API service module for connecting and managing social media accounts.
 */

import { apiClient, extractData } from './api';

// ─── Types ──────────────────────────────────────────────────────────────────

export type SocialPlatform =
  | 'facebook'
  | 'instagram'
  | 'x'
  | 'linkedin'
  | 'youtube'
  | 'bluesky'
  | 'tiktok'
  | 'pinterest'
  | 'reddit'
  | 'devto'
  | 'dailydev'
  | 'mastodon'
  | 'hashnode';

export type SocialAccountStatus = 'connected' | 'expired' | 'revoked' | 'error' | 'disconnected';

export type SocialAccountType = 'page' | 'profile' | 'business';

export interface ConnectedByUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  avatarUrl?: string | null;
}

export interface SocialAccountListItem {
  id: string;
  platform: SocialPlatform;
  platformAccountId: string;
  accountName: string | null;
  accountHandle: string | null;
  accountType: SocialAccountType;
  avatarUrl: string | null;
  status: SocialAccountStatus;
  healthStatus: Record<string, unknown>;
  followerCount: number;
  lastSyncAt: string | null;
  tokenExpiresAt: string | null;
  connectedBy: string | null;
  createdAt: string;
  updatedAt: string;
  connectedByUser: ConnectedByUser | null;
}

export interface SocialAccount {
  id: string;
  workspaceId: string;
  platform: SocialPlatform;
  accountName?: string | null;
  accountHandle?: string | null;
  avatarUrl?: string | null;
  status: SocialAccountStatus;
  followerCount: number;
  lastSyncAt?: string | null;
  tokenExpiresAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ConnectAccountRequest {
  platform: SocialPlatform;
  authorizationCode: string;
  redirectUri: string;
}

export interface PermissionDescription {
  label: string;
  description: string;
}

export interface ConnectUrlResponse {
  authorizationUrl: string;
  platform: string;
  scopes: string[];
  permissionDescriptions: PermissionDescription[];
}

export type TokenStatus = 'healthy' | 'expiring_soon' | 'expired' | 'no_expiry';

export interface RateLimitInfo {
  rateTier: string | null;
  limit: number | null;
  remaining: number | null;
  reset: number | null;
  lastChecked: string | null;
}

export interface AccountHealthData {
  id: string;
  platform: SocialPlatform;
  accountName: string | null;
  accountHandle: string | null;
  accountType: string;
  avatarUrl: string | null;
  status: SocialAccountStatus;
  followerCount: number;
  lastSyncAt: string | null;
  tokenExpiresAt: string | null;
  tokenExpiresInSeconds: number | null;
  tokenStatus: TokenStatus;
  healthStatus: Record<string, unknown>;
  rateLimitInfo: RateLimitInfo | null;
  recentErrorCount: number;
  lastError: string | null;
  lastTokenRefresh: string | null;
  consecutiveRefreshFailures: number;
  createdAt: string;
  updatedAt: string;
}

export interface HealthSummary {
  totalAccounts: number;
  healthyCount: number;
  expiringSoonCount: number;
  expiredCount: number;
  errorCount: number;
  platformBreakdown: Record<string, {
    total: number;
    healthy: number;
    issues: number;
  }>;
}

export interface DisconnectAccountResult {
  id: string;
  platform: string;
  accountName: string | null;
  accountHandle: string | null;
  status: string;
  cancelledScheduledPosts: number;
}

export interface RefreshAccountResult {
  id: string;
  platform: string;
  accountName: string | null;
  accountHandle: string | null;
  status: string;
  tokenExpiresAt: string | null;
  message: string;
}

// ─── Standalone Functions ───────────────────────────────────────────────────

/**
 * List all connected social accounts for a workspace.
 */
export const listSocialAccounts = (workspaceId: string): Promise<SocialAccountListItem[]> =>
  extractData(
    apiClient.get<SocialAccountListItem[]>(`/workspaces/${workspaceId}/social-accounts`)
  );

/**
 * Fetch all connected social accounts for a workspace.
 * Alias used by PublishNowModal and other publishing components.
 */
export const fetchSocialAccounts = (workspaceId: string): Promise<SocialAccountListItem[]> =>
  extractData(
    apiClient.get<SocialAccountListItem[]>(`/workspaces/${workspaceId}/social-accounts`)
  );

// ─── Social Account Service ─────────────────────────────────────────────────

export const socialAccountService = {
  /**
   * List all connected social accounts for a workspace.
   */
  list: (workspaceId: string): Promise<SocialAccountListItem[]> => listSocialAccounts(workspaceId),

  /**
   * Get a single social account by ID.
   */
  getById: (workspaceId: string, accountId: string): Promise<SocialAccount> =>
    extractData(
      apiClient.get<SocialAccount>(`/workspaces/${workspaceId}/social-accounts/${accountId}`)
    ),

  /**
   * Initiate OAuth connection flow. Returns the OAuth authorization URL
   * along with platform scopes and permission descriptions.
   */
  getConnectUrl: (
    workspaceId: string,
    platform: SocialPlatform
  ): Promise<ConnectUrlResponse> =>
    extractData(
      apiClient.get<ConnectUrlResponse>(
        `/workspaces/${workspaceId}/social-accounts/connect/${platform}`
      )
    ),

  /**
   * Complete the OAuth callback and store the account connection.
   */
  connect: (workspaceId: string, data: ConnectAccountRequest): Promise<SocialAccount> =>
    extractData(
      apiClient.post<SocialAccount>(`/workspaces/${workspaceId}/social-accounts`, data)
    ),

  /**
   * Disconnect (remove) a social account.
   * Revokes platform token, updates status to 'disconnected',
   * cancels scheduled posts. Historical data is preserved.
   */
  disconnect: (workspaceId: string, accountId: string): Promise<DisconnectAccountResult> =>
    extractData(
      apiClient.delete<DisconnectAccountResult>(
        `/workspaces/${workspaceId}/social-accounts/${accountId}`
      )
    ),

  /**
   * Refresh the OAuth tokens or validate the connection for an account.
   */
  refreshTokens: (workspaceId: string, accountId: string): Promise<RefreshAccountResult> =>
    extractData(
      apiClient.post<RefreshAccountResult>(
        `/workspaces/${workspaceId}/social-accounts/${accountId}/refresh`
      )
    ),

  /**
   * Get health monitoring data for all social accounts in a workspace.
   */
  getHealth: (workspaceId: string): Promise<AccountHealthData[]> =>
    extractData(
      apiClient.get<AccountHealthData[]>(
        `/workspaces/${workspaceId}/social-accounts/health`
      )
    ),
};